import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  resolveBusinessDate,
  type BondSource,
  type BondsResponseDto,
  type BondsRowDto,
  type CreateSafeTransactionInput,
  type SafeBalanceDto,
  type SafeTransactionDto,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import type { ReportRangeState } from '@/features/reports/useReports';

/**
 * Safe & Bonds data access (SPEC §12.1, wave 3).
 *
 * Every hook keeps an ERROR distinct from an empty result — the
 * error-before-empty rule the box-balance screen established: a section that
 * silently vanishes on a 500/403 reads as "no money moved", which is a claim
 * about money nobody verified.
 */

/* -------------------------------------------------------------------------- */
/* The safe                                                                   */
/* -------------------------------------------------------------------------- */

export function useSafeBalance() {
  const { t } = useTranslation();
  const [data, setData] = useState<SafeBalanceDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<SafeBalanceDto>('/safe'));
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.message
          ? caught.message
          : t('safe.balanceCard.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading, reload: load };
}

export function useSafeTransactions() {
  const { t } = useTranslation();
  const [data, setData] = useState<SafeTransactionDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<SafeTransactionDto[]>('/safe/transactions'));
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.message
          ? caught.message
          : t('safe.transactions.loadFailed'),
      );
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, reload: load };
}

/** Amount crosses the wire positive; the server signs it by kind. */
export function recordSafeTransaction(
  input: CreateSafeTransactionInput,
): Promise<SafeTransactionDto> {
  return api.post<SafeTransactionDto>('/safe/transactions', input);
}

/**
 * Whole-IQD amount field -> a validated positive integer, or null while the
 * input is empty/non-numeric. Same discipline as the reset dialog's counted
 * field: digits only, BigInt-safe, never a float.
 */
export function iqdAmountOf(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null;
  const amount = Number(raw);
  if (!Number.isSafeInteger(amount) || amount < 1) return null;
  return amount;
}

/* -------------------------------------------------------------------------- */
/* Bonds                                                                      */
/* -------------------------------------------------------------------------- */

export interface BondsWindow {
  /** Business dates, YYYY-MM-DD — what `GET /bonds` takes. */
  from: string;
  to: string;
}

export function useBonds(window: BondsWindow | null) {
  const { t } = useTranslation();
  const [data, setData] = useState<BondsResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!window) return;
    const token = ++requestId.current;
    setLoading(true);
    try {
      const response = await api.get<BondsResponseDto>(
        `/bonds?from=${window.from}&to=${window.to}`,
      );
      if (token === requestId.current) {
        setData(response);
        setError(null);
      }
    } catch (caught) {
      if (token === requestId.current) {
        setError(
          caught instanceof ApiError && caught.message
            ? caught.message
            : t('safe.bonds.loadFailed'),
        );
      }
    } finally {
      if (token === requestId.current) setLoading(false);
    }
  }, [window?.from, window?.to, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading, reload: load };
}

/** The venue's business-day settings, when the caller could learn them. */
export interface BusinessDayInfo {
  openingTime: string;
  closingTime: string;
  timeZone?: string;
}

/**
 * Map the shared range picker's From/To instants onto the business dates the
 * bonds endpoint takes.
 *
 * With the venue's business-day config known (the caller holds
 * `reports.view` and the catalogue loaded) this resolves EXACTLY as the
 * server's reports do: "26/7 02:00" lands on business date 2026-07-25.
 * Without it, the calendar date of each picked instant is used — a slightly
 * wider window, never a wrong-money one, and the resolved dates are echoed
 * under the picker either way so the reader always sees which business days
 * were queried.
 */
export function rangeToBusinessDates(
  range: ReportRangeState,
  businessDay: BusinessDayInfo | null,
): BondsWindow | null {
  const from = new Date(range.from);
  const to = new Date(range.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  const resolve = (instant: Date): string =>
    businessDay ? resolveBusinessDate(instant, businessDay) : localDatePart(instant);

  const a = resolve(from);
  const b = resolve(to);
  // A reversed range is a picker mistake, not an empty ledger — same rule as
  // `resolveReportRange` server-side.
  return a <= b ? { from: a, to: b } : { from: b, to: a };
}

/** Local calendar date of an instant — the staff PC runs on the venue's clock. */
function localDatePart(instant: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`;
}

/* -------------------------------------------------------------------------- */
/* Client-side filtering — chips work on the fetched window instantly         */
/* -------------------------------------------------------------------------- */

export interface BondsFilter {
  /** Empty set means "all sources". */
  sources: ReadonlySet<BondSource>;
  /** Null means "all employees". */
  employeeName: string | null;
}

export function filterBondsRows(rows: readonly BondsRowDto[], filter: BondsFilter): BondsRowDto[] {
  return rows.filter(
    (row) =>
      (filter.sources.size === 0 || filter.sources.has(row.source)) &&
      (filter.employeeName === null || row.employeeName === filter.employeeName),
  );
}

/** Exact signed sum of the given rows, as a millidinar string. BigInt only. */
export function sumBondsRows(rows: readonly BondsRowDto[]): string {
  let total = 0n;
  for (const row of rows) total += BigInt(row.amountMd);
  return total.toString();
}

/** Distinct employee names present in the window, for the filter dropdown. */
export function employeeNamesOf(rows: readonly BondsRowDto[]): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    if (row.employeeName) names.add(row.employeeName);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

/* -------------------------------------------------------------------------- */
/* CSV                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Millidinars -> an exact decimal IQD string ("12345678" -> "12345.678",
 * "-2000000" -> "-2000"). Pure string/BigInt arithmetic — a float here would
 * corrupt the very export an owner reconciles against.
 */
export function mdToIqdString(md: string): string {
  const value = BigInt(md);
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / 1000n;
  const remainder = magnitude % 1000n;
  const fraction =
    remainder === 0n ? '' : `.${remainder.toString().padStart(3, '0')}`.replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

/**
 * The shown rows as CSV. Built client-side: the bonds endpoint has no server
 * exporter this wave, and the shown rows — filters applied — are exactly what
 * the reader has just verified on screen.
 */
export function bondsCsv(rows: readonly BondsRowDto[]): string {
  const header = [
    'occurredAt',
    'businessDate',
    'source',
    'reference',
    'description',
    'employee',
    'customer',
    'method',
    'amountIqd',
    'amountMd',
  ];

  const lines = rows.map((row) =>
    [
      row.occurredAt,
      row.businessDate,
      row.source,
      row.reference ?? '',
      row.description,
      row.employeeName ?? '',
      row.customerName ?? '',
      row.method ?? '',
      mdToIqdString(row.amountMd),
      row.amountMd,
    ]
      .map(csvCell)
      .join(','),
  );

  return [header.join(','), ...lines].join('\r\n');
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Trigger a client-side download of the CSV. */
export function downloadBondsCsv(rows: readonly BondsRowDto[], window: BondsWindow): void {
  // The BOM makes Excel read the UTF-8 Arabic/Kurdish names correctly.
  const blob = new Blob(['﻿', bondsCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `bonds-${window.from}-to-${window.to}.csv`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
